import React from 'react'
import clsx from 'clsx'
import moment from 'moment'
import { BiCheckDouble } from 'react-icons/bi'
import { ClipboardTick, Messages2, Message } from 'iconsax-react'
import { useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import { selectDMs } from '@SonarCX/slices/dms'
import { markRead as markNotificationRead } from '@SonarCX/slices/notification'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { hasMention } from '@SonarCX/utilities'
import useStyles from './styles'

type NotificationPanelProps = {
  notification: any
  isLast: boolean
  onMobile: boolean
  onSelect?: (notification: any) => void
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  notification,
  isLast,
  onMobile,
  onSelect
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const { dms } = useAppSelector(selectDMs)

  const { allUsers, loadUser } = useUserAPI()

  const [user, setUser] = React.useState(null)

  const title = React.useMemo(() => {
    if (notification.source === 'task') {
      if (notification.title && hasMention(notification.title)) {
        return notification.body
      }

      return notification.title ?? notification.body ?? 'Notification'
    }

    if (!user?.name) {
      return 'Notification'
    }

    return `${user.name} mentioned you`
  }, [user, notification])

  const classes = useStyles({ isLast, onMobile })

  const triggerTime = moment(notification.stamp).fromNow()

  const handleMarkRead = React.useCallback(() => {
    dispatch(markNotificationRead(notification.id))
  }, [dispatch, notification.id])

  // Loads user if needed
  React.useEffect(() => {
    if (user) {
      return
    }

    const current = allUsers[notification.src_user_id]
    if (current) {
      setUser(current)
      return
    }

    loadUser(notification.src_user_id)
  }, [user, allUsers, notification.src_user_id, loadUser])

  const isPeerChat = React.useMemo(
    () => Object.keys(dms).includes(notification.source_session_id),
    [dms, notification.source_session_id]
  )

  const Icon = React.useMemo(() => {
    if (notification.source === 'task') {
      return ClipboardTick
    }

    return isPeerChat ? Messages2 : Message
  }, [notification.source, isPeerChat])

  return (
    <div
      onClick={() => {
        if (onSelect) {
          onSelect(notification)
        }
      }}
      className={classes.container}
    >
      <div className={clsx(classes.root, onMobile && classes.rootMobile)}>
        <div className={classes.avatar}>
          <Icon
            size={theme.size.xl}
            variant='Bulk'
            color='#5E5E5E'
            className='icon'
          />
        </div>

        <div className={classes.content}>
          <div className={clsx(classes.title, classes.clampWidth)}>{title}</div>

          <div className={classes.stamp}>{triggerTime}</div>
        </div>

        <div
          onClick={e => {
            e.stopPropagation()
            handleMarkRead()
          }}
          className={classes.check}
        >
          <BiCheckDouble
            size={theme.size.xl}
            color={theme.palette.primary.main}
          />
        </div>
      </div>

      {!isLast && <div className={classes.separator} />}
    </div>
  )
}

export default NotificationPanel
